/** Current coaching requests and recorded customer payments use owner projections. */
import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAuth } from "@bw/shared/hooks";
import { formatMoney } from "@bw/shared/utils";
import { useQuery } from "@tanstack/react-query";
import { getCoachIncome, type CoachIncome } from "@/libs/coachingApi";
import { getClubCoachExperience } from "@/libs/staffApi";
import { useMyClubCoachProfile } from "@/hooks/use-my-club-coach-profile";
import { CoachAvailabilityEditor } from "./coach-availability-editor";
import { CoachingWorkspaceInbox } from "./coaching-workspace-inbox";
import { CoachInquiryPreview } from "./coach-inquiry-preview";
import { useMyOrg } from "@/hooks/use-my-org";
import { Link } from "react-router-dom";
import { playerAppOrigin } from "@/libs/playerAppOrigin";

const sourceLabels = {
  legacy_lesson: "Earlier lesson bookings",
  coaching_agreement: "Coaching agreements",
  catalog_program: "Classes",
};

function IncomeBreakdown({ income }: { income: CoachIncome }) {
  if (!income.currencies.length)
    return (
      <Typography color="text.secondary">
        No recorded payments or refunds for this period.
      </Typography>
    );
  return (
    <Stack spacing={2}>
      <TableContainer>
        <Table size="small" aria-label="Payments by currency">
          <TableHead>
            <TableRow>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Collected</TableCell>
              <TableCell align="right">Refunded</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell align="right">Payments</TableCell>
              <TableCell align="right">Refunds</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {income.currencies.map((row) => (
              <TableRow key={row.currency}>
                <TableCell component="th" scope="row">
                  {row.currency.toUpperCase()}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(row.collected, row.currency)}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(row.refunded, row.currency)}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(row.net, row.currency)}
                </TableCell>
                <TableCell align="right">{row.payment_count}</TableCell>
                <TableCell align="right">{row.refund_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {income.sources.length ? (
        <TableContainer>
          <Table size="small" aria-label="Payments by source">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell align="right">Collected</TableCell>
                <TableCell align="right">Refunded</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">Payments</TableCell>
                <TableCell align="right">Refunds</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {income.sources.map((row) => (
                <TableRow key={`${row.kind}:${row.currency}`}>
                  <TableCell component="th" scope="row">
                    {sourceLabels[row.kind]} · {row.currency.toUpperCase()}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.collected, row.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.refunded, row.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.net, row.currency)}
                  </TableCell>
                  <TableCell align="right">{row.payment_count}</TableCell>
                  <TableCell align="right">{row.refund_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Stack>
  );
}

function IncomeCard() {
  const { user } = useAuth();
  const [month, setMonth] = useState("");
  const query = useQuery({
    queryKey: ["coaching-income", user?.uid, month],
    queryFn: () => getCoachIncome(month || undefined),
    enabled: Boolean(user),
    retry: false,
  });
  return (
    <Paper
      aria-label="Coach income"
      component="section"
      variant="outlined"
      sx={{ p: 3 }}
    >
      <Typography component="h2" variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        Coaching payments
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Reporting month (UTC)"
          type="month"
          size="small"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
          helperText="Leave empty for all time."
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ maxWidth: 280 }}
        />
        <Typography variant="body2" color="text.secondary">
          Customer payments and completed refunds, recorded in UTC. Class
          payments follow the assigned club coach profile. Your facility settles
          coach payouts off-platform.
        </Typography>
        {query.isPending ? (
          <Stack
            direction="row"
            role="status"
            spacing={1.5}
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={20} />
            <Typography color="text.secondary" variant="body2">
              Loading income…
            </Typography>
          </Stack>
        ) : query.isError ? (
          <Alert severity="error">
            Couldn’t load your income. Please try again.
          </Alert>
        ) : query.data ? (
          <IncomeBreakdown income={query.data} />
        ) : null}
      </Stack>
    </Paper>
  );
}

export function CoachWorkspace() {
  const { user } = useAuth();
  const actorId = user?.uid ?? "";
  const organization = useMyOrg();
  const orgId = organization.org?.org_id;
  const profile = useMyClubCoachProfile(orgId);
  // A 404 resolves to null: the caller has no club coach profile here.
  const missing = profile.isSuccess && profile.data === null;
  const known = profile.isSuccess && Boolean(profile.data);
  const facilityId = profile.data?.facilities?.[0]?.facility_id;
  const unavailable = profile.isError;
  const experience = useQuery({
    queryKey: ["coaching-experience", profile.data?.coach_profile_id],
    queryFn: () => getClubCoachExperience(profile.data!.coach_profile_id),
    enabled: known,
    retry: false,
  });
  if (!user)
    return (
      <Alert severity="info">Sign in to view your coaching workspace.</Alert>
    );
  if (organization.kind === "loading") return <Typography role="status">Loading your workspace…</Typography>;
  if (!orgId) return <Stack spacing={2}>
    <Typography component="h1" variant="h4">Coaching</Typography>
    <Alert severity={organization.kind === "error" ? "error" : "info"}>
      {organization.kind === "error" ? "We couldn’t load your organization access." : "Choose an organization to view your coaching profile and work there."}
    </Alert>
    <Button component={Link} to="/workspaces" sx={{ alignSelf: "flex-start" }}>Choose workspace</Button>
    <Button component="a" href={`${playerAppOrigin()}/me/coaching-experience`} sx={{ alignSelf: "flex-start" }}>Personal coaching record</Button>
  </Stack>;
  return (
    <Stack spacing={3} sx={{ pb: 5, width: "100%" }}>
      <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
        Coach Workspace
      </Typography>
      <Paper component="section" variant="outlined" sx={{ p: 2 }}><Stack spacing={1.5}>
        <Typography component="h2" variant="h6">Your personal coaching record</Typography>
        <Typography color="text.secondary" variant="body2">Your bio, photo, and verified experience stay with your account. Club offerings, rates, and ratings stay with the club.</Typography>
        <Button component="a" href={`${playerAppOrigin()}/me/coaching-experience`} sx={{ alignSelf: "flex-start" }}>Open personal profile and experience</Button>
      </Stack></Paper>
      <CoachInquiryPreview />
      {known ? (
        <Paper
          aria-label="Club-attested coaching experience"
          component="section"
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ justifyContent: "space-between" }}
            >
              <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                Teaching experience
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Club-attested
              </Typography>
            </Stack>
            {experience.isPending ? (
              <Typography role="status" color="text.secondary">
                Loading teaching experience…
              </Typography>
            ) : experience.isError ? (
              <Alert severity="error">Couldn’t load teaching experience.</Alert>
            ) : experience.data ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
                <Stack>
                  <Typography variant="h5">
                    {Math.floor(experience.data.coaching_minutes / 60)}h{" "}
                    {experience.data.coaching_minutes % 60}m
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Attributable coaching time
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5">
                    {experience.data.sessions_taught}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed or partial sessions
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="h5">
                    {experience.data.unique_students}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique students at this club
                  </Typography>
                </Stack>
              </Stack>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              Based on reviewed session records and present or late attendance.
              Scheduled or paid sessions are not counted automatically.
            </Typography>
          </Stack>
        </Paper>
      ) : null}
      {known ? (
        profile.data?.status === "published" ? (
          <IncomeCard key={`income:${actorId}`} />
        ) : (
          <Alert severity="info">
            Your club needs to publish your coaching profile before payments
            reporting is available.
          </Alert>
        )
      ) : missing ? (
        <Paper
          aria-label="Coach registration"
          component="section"
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ mb: 1, fontWeight: 700 }}
          >
            You’re not registered as a coach
          </Typography>
          <Typography color="text.secondary" variant="body2">
            A coach profile is needed for income and availability. You can
            manage lesson requests when your facility permissions allow it.
          </Typography>
        </Paper>
      ) : unavailable ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void profile.refetch()}
            >
              Retry
            </Button>
          }
        >
          Couldn’t load your coach profile. Please try again.
        </Alert>
      ) : (
        <Typography role="status">Checking coach profile…</Typography>
      )}
      <CoachingWorkspaceInbox key={`requests:${actorId}`} />
      {known && facilityId && profile.data ? (
        <CoachAvailabilityEditor
          coachProfileId={profile.data.coach_profile_id}
          facilityId={facilityId}
          key={`availability:${profile.data.coach_profile_id}:${facilityId}`}
        />
      ) : null}
    </Stack>
  );
}
